import { useCallback, useEffect, useState } from "react";
import { WorkExperience } from "../../../__generated__/types";
import { Button, Stack, Typography, Alert, Box } from "@mui/material";
import { WorkExperienceFormComponent, WorkExperienceValidations } from "./form";
import { useDesignerApplicationContext } from "../../../hooks/designer-application-context";
import { StaticWorkExperience } from "./static-work-experience";
import { validateWorkExperience, isWorkExperienceValid } from "../validations";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

export const WorkExperienceForm = ({
  hideNewForm = false,
  isEditable = true,
  onValidationChange,
}: {
  hideNewForm?: boolean;
  isEditable?: boolean;
  validations?: WorkExperienceValidations;
  onValidationChange?: (isValid: boolean) => void;
}) => {
  const ctx = useDesignerApplicationContext();
  const { updateApplicationData } = ctx;
  const application = (ctx as any).application ?? {};
  const [currentForm, setCurrentForm] = useState<WorkExperience | null>(null);

  const initialValue = (application?.experience ?? []) as WorkExperience[];
  const [experienceList, setExperienceList] = useState<WorkExperience[]>(initialValue);

  useEffect(() => {
    setExperienceList(initialValue);
  }, [application?.experience]);

  const onChange = useCallback(
    (newValue: WorkExperience[]) => {
      setExperienceList(newValue);
      // Update application data immediately to trigger validation
      updateApplicationData({
        ...(application || {}),
        experience: newValue,
      });
      setCurrentForm(null);
    },
    [application, updateApplicationData]
  );

  // If the current form ID isn't found, fall back to appending at the end
  const foundIdx = currentForm?.id
    ? experienceList.findIndex((exp) => exp?.id && exp.id === currentForm?.id)
    : -1;
  const currentFormIndex = foundIdx >= 0 ? foundIdx : experienceList.length;
  const isEditingExisting = foundIdx >= 0;

  const fmt = (d?: string | null) => (d ? String(d) : "");

  // Validate work experience
  const validation = validateWorkExperience(experienceList);

  // Notify parent component of validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validation.isValid);
    }
  }, [validation.isValid, onValidationChange]);

  if (!isEditable) {
    return <StaticWorkExperience value={experienceList} />;
  }

  return (
    <>
      {/* Validation Info */}
      {!validation.isValid && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Work Experience Requirements:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {validation.errors.map((error, index) => (
              <li key={index}>
                <Typography variant="body2" style={{ color: '#D97706' }}>{error}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {!hideNewForm && (
        <Button
          variant="outlined"
          onClick={() => setCurrentForm({} as WorkExperience)}
          sx={{ mb: 2 }}
        >
          Add Work Experience
        </Button>
      )}
      {experienceList.map((experience, index) => (
        <div
          className="work-experience-item"
          key={experience?.id ?? `${experience?.company ?? "co"}-${index}`}
        >
          {currentForm?.id === experience?.id ? (
            <WorkExperienceFormComponent
              value={experienceList}
              onChange={onChange}
              currentForm={currentForm}
              setCurrentForm={setCurrentForm}
              currentFormIndex={currentFormIndex}
            />
          ) : (
            <Stack direction="row" gap={2} justifyContent="space-between">
              <div>
                <h4 className="work-experience-title font-body font-size-4">
                  {experience?.title || "Untitled Role"}
                </h4>
                <p className="font-size-3 margin-0">
                  {experience?.company || "Company"}
                </p>
                <p className="font-size-3 margin-0">
                  {fmt(experience?.startDate)}{" - "}{fmt(experience?.endDate)}
                </p>
              </div>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="text"
                  onClick={() => setCurrentForm(experience)}
                  sx={{
                    minWidth: 44,
                    height: 44,
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    const newList = experienceList.filter((_, i) => i !== index);
                    onChange(newList);
                  }}
                  sx={{
                    minWidth: 44,
                    height: 44,
                    padding: 0,
                    color: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </Stack>
            </Stack>
          )}
        </div>
      ))}
      {currentForm && !isEditingExisting && (
        <div className="work-experience-item">
          <WorkExperienceFormComponent
            value={experienceList}
            onChange={onChange}
            currentForm={currentForm}
            setCurrentForm={setCurrentForm}
            currentFormIndex={currentFormIndex}
          />
        </div>
      )}
    </>
  );
};
